import {
  type CorrectReceiptInput,
  type Page as PageDto,
  PERMISSIONS,
  type ReviewItem,
} from '@edms/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PageBody, PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/ui/data-table';
import { useToast } from '@/components/ui/overlays';
import { Button, Field, Input } from '@/components/ui/primitives';
import { Badge, Card, EmptyState, Skeleton } from '@/components/ui/surfaces';
import { DocumentViewer } from '@/features/receipts/document-viewer';
import { useAuth } from '@/features/auth/auth-context';
import { api, ApiError } from '@/lib/api-client';
import { cn, formatPercent, formatRelative } from '@/lib/utils';

const REASON_TONES = {
  OCR_FAILED: 'danger',
  LOW_CONFIDENCE: 'warning',
  MISSING_REQUIRED_FIELD: 'warning',
  DUPLICATE: 'info',
} as const;

/**
 * Validation manuelle des exceptions (§4, §6).
 *
 * Disposition en deux volets : la file à gauche, le document et le formulaire
 * de correction à droite. L'opérateur corrige un numéro en lisant l'image, sans
 * changer d'écran ni perdre sa place dans la file — c'est le geste qu'il
 * répétera des dizaines de fois par jour.
 */
export function ReviewPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('receipt'));

  const lotId = searchParams.get('lot') ?? undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['review', { page, lotId }],
    queryFn: () =>
      api.get<PageDto<ReviewItem>>('/review', { page, pageSize: 25, ...(lotId ? { lotId } : {}) }),
    placeholderData: keepPreviousData,
    refetchInterval: 20_000,
  });

  const items = data?.items ?? [];
  const selected = items.find((item) => item.receiptId === selectedId) ?? items[0] ?? null;

  // Après traitement d'une exception, la sélection glisse sur la suivante :
  // l'opérateur enchaîne sans cliquer.
  useEffect(() => {
    if (selectedId && !items.some((item) => item.receiptId === selectedId)) {
      setSelectedId(items[0]?.receiptId ?? null);
    }
  }, [items, selectedId]);

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <PageHeader
          title="Validation des exceptions"
          description="Documents dont la reconnaissance demande une confirmation humaine"
          actions={
            data && data.total > 0 ? (
              <Badge tone="warning" dot>
                {data.total} en attente
              </Badge>
            ) : undefined
          }
        />

        <PageBody>
          {isLoading ? (
            <Card className="space-y-3">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </Card>
          ) : items.length === 0 ? (
            <Card>
              <EmptyState
                icon={CheckCircle2}
                title="Aucune exception en attente"
                description="Tous les documents traités ont été reconnus avec un niveau de confiance suffisant."
              />
            </Card>
          ) : (
            <div className="overflow-hidden rounded-lg border border-line-subtle bg-raised shadow-[var(--shadow-card)]">
              <ul className="divide-y divide-line-subtle">
                {items.map((item) => (
                  <li key={item.receiptId}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(item.receiptId)}
                      className={cn(
                        'flex w-full items-center gap-4 px-4 py-3 text-left transition-colors',
                        selected?.receiptId === item.receiptId
                          ? 'bg-accent-soft'
                          : 'hover:bg-hover',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-ink">
                            {item.suggestedOrderNumber ?? (
                              <span className="text-ink-faint italic">numéro non lu</span>
                            )}
                          </span>
                          <Badge tone={REASON_TONES[item.reason]}>{item.reasonLabel}</Badge>
                        </div>
                        <p className="mt-0.5 text-2xs text-ink-faint">
                          {item.branchCode} · lot {item.lotReference} · {item.pageCount} page(s) ·{' '}
                          {formatRelative(item.createdAt)}
                        </p>

                        {/*
                          Le pourcentage global dit qu'un doute existe ; cette
                          ligne dit lequel. C'est la différence entre relire
                          huit champs et en relire un.
                        */}
                        {champsNonConformes(item).length > 0 && (
                          <p className="mt-1 flex items-center gap-1.5 text-2xs font-medium text-warning">
                            <AlertTriangle className="size-3 shrink-0" />
                            <span className="truncate">
                              format inattendu : {champsNonConformes(item).join(', ')}
                            </span>
                          </p>
                        )}
                      </div>

                      <span className="shrink-0 text-xs text-ink-muted">
                        {formatPercent(item.overallConfidence)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              {data && (
                <Pagination
                  page={data.page}
                  pageSize={data.pageSize}
                  total={data.total}
                  totalPages={data.totalPages}
                  onPageChange={setPage}
                />
              )}
            </div>
          )}
        </PageBody>
      </div>

      {selected && (
        <div className="anim-in fixed inset-0 z-40 flex flex-col border-l border-line-subtle bg-raised xl:static xl:z-auto xl:w-[38rem] xl:shrink-0">
          <div className="min-h-0 flex-1 overflow-hidden">
            <DocumentViewer receiptId={selected.receiptId} onClose={() => setSelectedId(null)} />
          </div>

          {can(PERMISSIONS.RECEIPTS_CORRECT) && (
            <CorrectionForm key={selected.receiptId} item={selected} />
          )}
        </div>
      )}
    </div>
  );
}

function CorrectionForm({ item }: { item: ReviewItem }): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [orderNumber, setOrderNumber] = useState(item.suggestedOrderNumber ?? '');
  const [subNumber, setSubNumber] = useState(item.suggestedSubNumber ?? '');
  const [deliveryDate, setDeliveryDate] = useState(
    item.fields.find((field) => field.fieldKey === 'delivery_date')?.normalizedValue ?? '',
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['review'] });
    void queryClient.invalidateQueries({ queryKey: ['receipt', item.receiptId] });
    void queryClient.invalidateQueries({ queryKey: ['lot'] });
  };

  const correct = useMutation({
    mutationFn: (input: CorrectReceiptInput) => api.patch(`/receipts/${item.receiptId}`, input),
    onError: (error) =>
      toast.error('Correction refusée', error instanceof ApiError ? error.message : undefined),
  });

  const validate = useMutation({
    mutationFn: () => api.post(`/receipts/${item.receiptId}/validate`),
    onSuccess: () => {
      toast.success('Document validé', 'Il est désormais archivé et consultable.');
      invalidate();
    },
    onError: (error) =>
      toast.error('Validation impossible', error instanceof ApiError ? error.message : undefined),
  });

  const saveAndValidate = async (): Promise<void> => {
    await correct.mutateAsync({
      orderNumber: orderNumber.trim() || null,
      subNumber: subNumber.trim() || null,
      deliveryDate: deliveryDate || null,
      reason: 'Validation manuelle depuis la file des exceptions',
    });
    validate.mutate();
  };

  const canValidate = can(PERMISSIONS.LOTS_VALIDATE);
  const identified = Boolean(orderNumber.trim() || subNumber.trim());

  return (
    <div className="shrink-0 border-t border-line-subtle bg-sunken/50 p-4">
      <p className="mb-3 text-2xs font-semibold tracking-wide text-ink-faint uppercase">
        Correction manuelle
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Numéro de commande" htmlFor="fix-order" required>
          <Input
            id="fix-order"
            size="sm"
            value={orderNumber}
            onChange={(event) => setOrderNumber(event.target.value)}
            className="font-mono"
            autoFocus
            placeholder="T2026 120548"
          />
        </Field>

        <Field label="Sous-numéro" htmlFor="fix-sub">
          <Input
            id="fix-sub"
            size="sm"
            value={subNumber}
            onChange={(event) => setSubNumber(event.target.value)}
            className="font-mono"
            placeholder="120548"
          />
        </Field>

        <Field
          label="Date de livraison"
          htmlFor="fix-date"
          className="col-span-2"
          hint="Renseignez-la : elle participe à la détection des doublons."
        >
          <Input
            id="fix-date"
            size="sm"
            type="date"
            value={deliveryDate}
            onChange={(event) => setDeliveryDate(event.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button
          size="sm"
          loading={correct.isPending}
          onClick={() =>
            correct.mutate(
              {
                orderNumber: orderNumber.trim() || null,
                subNumber: subNumber.trim() || null,
                deliveryDate: deliveryDate || null,
              },
              { onSuccess: () => { toast.success('Métadonnées enregistrées'); invalidate(); } },
            )
          }
        >
          Enregistrer
        </Button>

        {canValidate && (
          <Button
            size="sm"
            variant="primary"
            icon={<Check className="size-4" />}
            disabled={!identified}
            loading={validate.isPending || correct.isPending}
            onClick={() => void saveAndValidate()}
          >
            Valider et archiver
          </Button>
        )}
      </div>

      {!identified && (
        <p className="mt-2 flex items-center gap-1.5 text-2xs text-ink-faint">
          <ShieldCheck className="size-3" aria-hidden />
          Un numéro de commande ou un sous-numéro est requis pour archiver.
        </p>
      )}
    </div>
  );
}

/**
 * Champs dont la valeur ne respecte pas le format attendu par le modèle OCR.
 *
 * Un sous-numéro qui ne commence pas par `T`, une date impossible : ces valeurs
 * sont fausses à coup sûr, indépendamment du taux de reconnaissance. Sur un lot
 * réel de 71 bons de livraison, ce contrôle isole les 12 reçus à relire et
 * laisse passer les 59 autres — c'est ce qui rend sûre la validation d'un lot
 * en un seul geste.
 */
function champsNonConformes(item: ReviewItem): string[] {
  return item.fields
    .filter((field) => field.patternMatched === false)
    .map((field) => field.label.toLowerCase());
}
